import { useState, useEffect } from 'react';
import { Hash, Address } from 'viem';
import { SupportedChainId } from '@packet/constants/AppSupportChains';
import { tokenPoolCache } from '@/utils/tokenPool';
import { ChainConfigMap } from '@/const/chains';

interface TokenInfo {
  symbol: string;
  name: string;
  decimals: number;
}

interface UseTokenInfoResult {
  tokenInfo: TokenInfo | null;
  loading: boolean;
  error: string | null;
}

export const useTokenInfo = (assetId: Hash, chainId: SupportedChainId): UseTokenInfoResult => {
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTokenInfo = async () => {
      try {
        setLoading(true);
        setError(null);

        // 找到对应的链配置
        const chain = ChainConfigMap[chainId];
        if (!chain || !chain.appContract) {
          throw new Error(`Unsupported chainId: ${chainId} or missing app contract`);
        }

        const contractAddress = chain.appContract as Address;

        // 通过 tokenPoolCache 获取pool信息
        const poolInfo = await tokenPoolCache.getPool(contractAddress, chainId, assetId);

        if (!isMounted) return;

        const tokenAddress = poolInfo.token;

        // 通过 tokenPoolCache 获取token完整信息
        const info = await tokenPoolCache.getTokenInfo(tokenAddress, chainId);

        if (isMounted) {
          setTokenInfo(info);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch token info:', err);
          setError(err instanceof Error ? err.message : 'Unknown error');
          setTokenInfo(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTokenInfo();

    return () => {
      isMounted = false;
    };
  }, [assetId, chainId]);

  return { tokenInfo, loading, error };
};
